import { RefObject, useEffect, useRef } from 'react';

interface UseLoadMoreOnScrollOptions {
  // Czy w ogóle nasłuchiwać (np. wyłącz gdy lista pusta albo lazyLoad=false).
  enabled?: boolean;
  // true → scroll całej strony (lista rośnie naturalnie); false → scroll
  // wewnątrz boxa (elementu spod `ref`).
  pageScroll?: boolean;
  // Z jakim wyprzedzeniem (px) od dołu zaczynamy doczytywać. Niewielkie — by
  // spinner/kolejna paczka zdążyły wejść w widok.
  offset?: number;
  // Zmiana tej wartości re-uruchamia początkowe sprawdzenie. Podaj długość
  // listy: po doczytaniu paczki od razu sprawdzamy, czy treść już wypełnia
  // ekran, a jeśli nie — dobieramy kolejną.
  recheckKey?: number;
}

// Z jakim wyprzedzeniem (px) od dołu domyślnie odpalamy doczytywanie.
const DEFAULT_OFFSET = 140;

/**
 * Wspólny mechanizm „load more on scroll" — ten sam, którego używa dziennik
 * aktywności (ActivityFeed) i centrum powiadomień (Notification).
 *
 * W wariancie stronicowym (`pageScroll`) słucha scrolla okna w fazie capture,
 * dzięki czemu łapie też scroll wewnętrznego kontenera treści, i woła
 * `onReachEnd`, gdy dół elementu spod `ref` zbliży się do dołu widoku. W
 * wariancie boxa nasłuchuje scrolla samego elementu. `onReachEnd` czytane jest
 * przez ref, więc handler nigdy nie łapie nieświeżego domknięcia.
 */
export const useLoadMoreOnScroll = (
  ref: RefObject<HTMLElement | null>,
  onReachEnd: () => void,
  {
    enabled = true,
    pageScroll = false,
    offset = DEFAULT_OFFSET,
    recheckKey = 0,
  }: UseLoadMoreOnScrollOptions = {},
): void => {
  const onReachEndRef = useRef(onReachEnd);
  onReachEndRef.current = onReachEnd;

  useEffect(() => {
    if (!enabled) return undefined;
    const el = ref.current;
    if (!el) return undefined;

    const fire = () => onReachEndRef.current();

    if (pageScroll) {
      const onPageScroll = () => {
        if (el.getBoundingClientRect().bottom <= window.innerHeight + offset) fire();
      };
      window.addEventListener('scroll', onPageScroll, { capture: true, passive: true });
      window.addEventListener('resize', onPageScroll);
      onPageScroll();
      return () => {
        window.removeEventListener('scroll', onPageScroll, { capture: true });
        window.removeEventListener('resize', onPageScroll);
      };
    }

    const onBoxScroll = () => {
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - offset) fire();
    };
    el.addEventListener('scroll', onBoxScroll, { passive: true });
    onBoxScroll();
    return () => el.removeEventListener('scroll', onBoxScroll);
  }, [ref, enabled, pageScroll, offset, recheckKey]);
};
